import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { 
    Box, Container, Typography, Card, CardContent, CardMedia, 
    Grid, Button, CircularProgress, Divider, Paper, List, ListItem, ListItemText 
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import WaterDropIcon from '@mui/icons-material/WaterDrop';
import Axios from 'axios';
import config, { environment } from '../../config';

function MineralWaterDetail() {
    const { category, id } = useParams();
    const navigate = useNavigate();
    const { i18n } = useTranslation();
    const [data, setData] = useState(null);
    const [isLoading, setIsLoading] = useState(true);

    useEffect(() => {
        const source = Axios.CancelToken.source();
        async function getDetail() {
            try {
                const endpoint = config[environment].getMineralWaterInfoEndpoint(category, id);
                const response = await Axios.get(endpoint, { cancelToken: source.token });
                setData(response.data);
                setIsLoading(false);
            } catch (error) {
                if (!Axios.isCancel(error)) {
                    console.error("Error fetching mineral water detail:", error);
                    setIsLoading(false);
                }
            }
        }
        getDetail();
        return () => source.cancel();
    }, [category, id]);

    if (isLoading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '80vh' }}>
                <CircularProgress />
            </Box>
        );
    }

    if (!data) {
        return (
            <Container sx={{ py: 8, textAlign: 'center' }}>
                <Typography variant="h5">Az objektum nem található.</Typography>
                <Button 
                    startIcon={<ArrowBackIcon />} 
                    onClick={() => navigate(`/${i18n.language}/feltarasok`)}
                    sx={{ mt: 2 }}
                >
                    Vissza a térképhez
                </Button>
            </Container>
        );
    }

    const name = data.nev || data.name || data.name_hu || data.field2 || "Névtelen ásványvíz";
    const description = data.wpt_descri || data.descriptio || data.note || "";

    return (
        <Container maxWidth="lg" sx={{ py: 4 }}>
            <Button 
                startIcon={<ArrowBackIcon />} 
                onClick={() => navigate(-1)}
                sx={{ mb: 3 }}
            >
                Vissza
            </Button>

            <Grid container spacing={4}>
                <Grid item xs={12} md={7}>
                    <Card>
                        {data.picture1 || data.image ? (
                            <CardMedia
                                component="img"
                                height="400"
                                image={data.picture1 || data.image}
                                alt={name}
                            />
                        ) : (
                            <Box sx={{ 
                                height: 300, 
                                bgcolor: 'grey.200', 
                                display: 'flex', 
                                alignItems: 'center', 
                                justifyContent: 'center' 
                            }}>
                                <WaterDropIcon sx={{ fontSize: 100, color: 'primary.light', opacity: 0.5 }} />
                            </Box>
                        )}
                        <CardContent>
                            <Typography variant="h4" component="h1" gutterBottom color="primary">
                                {name}
                            </Typography>
                            <Typography variant="subtitle1" color="text.secondary" gutterBottom>
                                {category}
                            </Typography>
                            <Divider sx={{ my: 2 }} />
                            <Typography variant="body1" paragraph sx={{ whiteSpace: 'pre-wrap' }}>
                                {description || "Nincs leírás elérhető ehhez az objektumhoz."}
                            </Typography>
                        </CardContent>
                    </Card>
                </Grid>

                <Grid item xs={12} md={5}>
                    <Paper sx={{ p: 3 }}>
                        <Typography variant="h6" gutterBottom>Részletek</Typography>
                        <List>
                            {data.name_ro && (
                                <ListItem disableGutters>
                                    <ListItemText primary="Román név" secondary={data.name_ro} />
                                </ListItem>
                            )}
                            {data.name_en && (
                                <ListItem disableGutters>
                                    <ListItemText primary="Angol név" secondary={data.name_en} />
                                </ListItem>
                            )}
                            {data.ele && (
                                <ListItem disableGutters>
                                    <ListItemText primary="Tengerszint feletti magasság" secondary={`${data.ele} m`} />
                                </ListItem>
                            )}
                            {data.taste && (
                                <ListItem disableGutters>
                                    <ListItemText primary="Íz" secondary={data.taste} />
                                </ListItem>
                            )}
                            {data.water_char && (
                                <ListItem disableGutters>
                                    <ListItemText primary="Víz jellege" secondary={data.water_char} />
                                </ListItem>
                            )}
                            {data.drinkable && (
                                <ListItem disableGutters>
                                    <ListItemText primary="Iható" secondary={data.drinkable} />
                                </ListItem>
                            )}
                             {data.temperature && (
                                <ListItem disableGutters>
                                    <ListItemText primary="Hőmérséklet" secondary={`${data.temperature} °C`} />
                                </ListItem>
                            )}
                        </List>
                        
                        {data.geom && (
                            <Box sx={{ mt: 2 }}>
                                <Typography variant="body2" color="text.secondary">
                                    Koordináták: {data.geom.coordinates[1].toFixed(5)}, {data.geom.coordinates[0].toFixed(5)}
                                </Typography>
                            </Box>
                        )}
                    </Paper>
                </Grid>
            </Grid>
        </Container>
    );
}

export default MineralWaterDetail;
